import { FunctionComponent, useCallback, useRef } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";

import { translationKeys } from "../../../../../models/app-translation-keys";
import { FormField, IFormFieldRules } from "../../FormField/FormField";

import "./TextInputField.scss";

interface ITextInputFormFieldRules extends IFormFieldRules {
  minLength?: number;
  maxLength?: number;
}

interface ITextInputProps {
  control: Control<any, any, any>;
  fieldNameTranslationKey: string;
  fieldDescriptionTranslationKey?: string;
  hideLabel?: boolean;
  fieldName: string;
  rules?: ITextInputFormFieldRules;
}

type TextInputFormFieldRules = Omit<
  RegisterOptions<any, any>,
  "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
>;

export const TextInputField: FunctionComponent<ITextInputProps> = (props: ITextInputProps) => {
  const { t } = useTranslation();

  const inputRef = useRef<HTMLInputElement>(null);

  const formFieldRules = (): TextInputFormFieldRules => {
    const rules: TextInputFormFieldRules = {};
    if (props.rules?.required) {
      rules.required = t(translationKeys.form.error.isRequired, {
        fieldName: t(props.fieldNameTranslationKey),
      });
    }

    if (props.rules?.minLength) {
      rules.minLength = {
        value: props.rules.minLength,
        message: t(translationKeys.form.error.minLength, {
          fieldName: t(props.fieldNameTranslationKey),
          minLength: props.rules.minLength,
        }),
      };
    }

    if (props.rules?.maxLength) {
      rules.maxLength = {
        value: props.rules.maxLength,
        message: t(translationKeys.form.error.maxLength, {
          fieldName: t(props.fieldNameTranslationKey),
          maxLength: props.rules.maxLength,
        }),
      };
    }

    return rules;
  };

  const hasCharacterCount = useCallback((): boolean => {
    return props.rules?.maxLength !== undefined;
  }, [props.rules?.maxLength]);

  return (
    <Controller
      name={props.fieldName}
      control={props.control}
      rules={formFieldRules()}
      render={({ field, fieldState: { error, invalid } }) => (
        <FormField
          fieldName={props.fieldName}
          fieldNameTranslationKey={props.fieldNameTranslationKey}
          fieldDescriptionTranslationKey={props.fieldDescriptionTranslationKey}
          hideLabel={props.hideLabel}
          error={error}
          invalid={invalid}
        >
          <div
            className={classNames("text-input-field", {
              "has-character-count": hasCharacterCount(),
            })}
          >
            <InputText
              id={field.name}
              {...field}
              value={field.value ?? ""}
              autoFocus
              className={classNames({ "p-invalid": invalid })}
              style={{
                paddingRight: hasCharacterCount()
                  ? `calc(${field.value.length.toString().length * 0.3}rem + 3.8rem)`
                  : "inherit",
              }}
              ref={inputRef}
            />

            {hasCharacterCount() && (
              <small className={classNames("character-count", { invalid: invalid })}>
                {!!props.rules?.maxLength && (
                  <span>
                    <span className="value-count">{field.value?.length ?? 0}</span> / {props.rules?.maxLength ?? 100}
                  </span>
                )}

                {!!props.rules?.minLength && !props.rules?.maxLength && <span>{field.value?.length ?? 0}</span>}
              </small>
            )}
          </div>
        </FormField>
      )}
    />
  );
};
